import React, { useState, useEffect } from 'react';
import { 
  Map as MapIcon, 
  TrendingUp, 
  Search,
  RefreshCw,
  MapPin,
  Home,
  Euro,
  Users,
  AlertCircle
} from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import { formatCurrency, formatPercent } from '../../utils/formatters';
import { Line, Scatter } from 'react-chartjs-2';

interface MarketData {
  postalCode: string;
  city: string;
  district: string;
  avgRentPerSqm: number;
  avgPricePerSqm: number;
  rentalYield: number;
  vacancy: number;
  population: number;
  growthRate: number;
  marketScore: number;
  comparables: ComparableProperty[];
}

interface ComparableProperty {
  id: string;
  address: string;
  size: number;
  rooms: number;
  price: number;
  rent: number;
  yearBuilt: number;
  condition: string;
  distance: number;
  similarity: number;
}

const MarketIntelligence: React.FC = () => {
  const { currentInputs } = useApp();
  const [marketData, setMarketData] = useState<MarketData | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedView, setSelectedView] = useState<'overview' | 'comparables' | 'trends' | 'heatmap'>('overview');
  const [searchPostalCode, setSearchPostalCode] = useState((currentInputs as any)?.postalCode || '10115');
  const [filters, setFilters] = useState({
    radius: 5,
    minSimilarity: 70,
    propertyType: 'all',
    condition: 'all'
  });

  // Simulated market data fetch
  useEffect(() => {
    const postalCode = (currentInputs as any)?.postalCode;
    if (postalCode) {
      fetchMarketData(postalCode);
    }
  }, [(currentInputs as any)?.postalCode]);

  const fetchMarketData = async (postalCode: string) => {
    setLoading(true);
    
    /* 
     * CURRENT STATUS: Using simulated data
     * 
     * REAL DATA SOURCES for production:
     * 
     * 1. ImmoScout24 API (https://api.immobilienscout24.de)
     *    - Requires API key and partner agreement
     *    - Provides: listings, prices, rental yields
     * 
     * 2. ImmobilienScout24 GeoAPI
     *    - Location-based property data
     *    - Market trends and statistics
     * 
     * 3. Immowelt API (https://api.immowelt.de)
     *    - Alternative data source
     *    - Similar offerings to ImmoScout24
     * 
     * 4. PriceHubble API (https://www.pricehubble.com/de/api/)
     *    - AI-powered valuations
     *    - Market analytics for DACH region
     * 
     * 5. Scoperty API (https://scoperty.de)
     *    - German property valuations
     *    - Neighborhood analytics
     * 
     * 6. Government Data:
     *    - Destatis (Federal Statistical Office)
     *    - Local Gutachterausschuss data
     *    - Mietspiegel databases
     * 
     * Implementation example:
     * const response = await fetch(`https://api.immobilienscout24.de/restapi/api/search/v1.0/search/region?realestatetype=apartmentrent&geocode=${postalCode}`, {
     *   headers: {
     *     'Authorization': `Bearer ${API_KEY}`,
     *     'Accept': 'application/json'
     *   }
     * });
     */
    
    // Simulate API call with mock data
    setTimeout(() => {
      const simulatedData: MarketData = {
        postalCode: postalCode || '10115',
        city: 'Berlin',
        district: 'Mitte',
        avgRentPerSqm: 16.50 + Math.random() * 5,
        avgPricePerSqm: 6500 + Math.random() * 2000,
        rentalYield: 3.5 + Math.random() * 1.5,
        vacancy: 1.5 + Math.random() * 2,
        population: 385000 + Math.floor(Math.random() * 50000),
        growthRate: 1.5 + Math.random() * 2,
        marketScore: 70 + Math.floor(Math.random() * 20),
        comparables: generateComparables()
      };
      setMarketData(simulatedData);
      setLoading(false);
    }, 1500);
  };

  const generateComparables = (): ComparableProperty[] => {
    return Array.from({ length: 20 }, (_, i) => ({
      id: `comp-${i}`,
      address: `Beispielstraße ${Math.floor(Math.random() * 100)}, 10115 Berlin`,
      size: 60 + Math.floor(Math.random() * 80),
      rooms: 2 + Math.floor(Math.random() * 3),
      price: 350000 + Math.floor(Math.random() * 300000),
      rent: 1000 + Math.floor(Math.random() * 1500),
      yearBuilt: 1950 + Math.floor(Math.random() * 70),
      condition: ['Sehr gut', 'Gut', 'Mittel', 'Renovierungsbedürftig'][Math.floor(Math.random() * 4)],
      distance: Math.random() * 5,
      similarity: 60 + Math.floor(Math.random() * 40)
    }));
  };


  const renderOverview = () => {
    if (!marketData) return null;

    return (
      <div className="space-y-6">
        {/* Market Score Card */}
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-bold mb-2">Markt-Score</h3>
              <p className="text-indigo-100">
                {marketData.district}, {marketData.city}
              </p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold">{marketData.marketScore}</div>
              <div className="text-indigo-100 mt-1">von 100</div>
            </div>
          </div>
        </div>

        {/* Key Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <Euro className="w-5 h-5 text-gray-400" />
              <span className="text-xs text-gray-500">Durchschnitt</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {formatCurrency(marketData.avgRentPerSqm)}/m²
            </div>
            <div className="text-sm text-gray-600 mt-1">Mietpreis</div>
          </div>

          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <Home className="w-5 h-5 text-gray-400" />
              <span className="text-xs text-gray-500">Durchschnitt</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {formatCurrency(marketData.avgPricePerSqm)}/m²
            </div>
            <div className="text-sm text-gray-600 mt-1">Kaufpreis</div>
          </div>

          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-5 h-5 text-gray-400" />
              <span className="text-xs text-green-500">+{marketData.growthRate.toFixed(1)}%</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {formatPercent(marketData.rentalYield / 100)}
            </div>
            <div className="text-sm text-gray-600 mt-1">Mietrendite</div>
          </div>

          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <Users className="w-5 h-5 text-gray-400" />
              <span className="text-xs text-gray-500">{formatPercent(marketData.vacancy / 100)}</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {marketData.population.toLocaleString('de-DE')}
            </div>
            <div className="text-sm text-gray-600 mt-1">Einwohner</div>
          </div>
        </div>

        {/* Market Trends Chart */}
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <h3 className="font-semibold text-gray-900 mb-4">Markttrends (12 Monate)</h3>
          <Line
            data={{
              labels: ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'],
              datasets: [
                {
                  label: 'Mietpreis/m²',
                  data: Array.from({ length: 12 }, () => marketData.avgRentPerSqm + (Math.random() - 0.5) * 2),
                  borderColor: 'rgb(99, 102, 241)',
                  backgroundColor: 'rgba(99, 102, 241, 0.1)',
                  yAxisID: 'y',
                },
                {
                  label: 'Kaufpreis/m²',
                  data: Array.from({ length: 12 }, () => marketData.avgPricePerSqm + (Math.random() - 0.5) * 500),
                  borderColor: 'rgb(239, 68, 68)',
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  yAxisID: 'y1',
                }
              ]
            }}
            options={{
              responsive: true,
              interaction: {
                mode: 'index' as const,
                intersect: false,
              },
              scales: {
                y: {
                  type: 'linear' as const,
                  display: true,
                  position: 'left' as const,
                  title: {
                    display: true,
                    text: 'Mietpreis €/m²'
                  }
                },
                y1: {
                  type: 'linear' as const,
                  display: true,
                  position: 'right' as const,
                  title: {
                    display: true,
                    text: 'Kaufpreis €/m²'
                  },
                  grid: {
                    drawOnChartArea: false,
                  },
                },
              },
            }}
          />
        </div>
      </div>
    );
  };

  const renderComparables = () => {
    if (!marketData) return null;

    const filteredComparables = marketData.comparables.filter(comp => 
      comp.similarity >= filters.minSimilarity &&
      comp.distance <= filters.radius
    );

    return (
      <div className="space-y-6">
        {/* Filters */}
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Filter</h3>
            <button
              onClick={() => fetchMarketData(marketData.postalCode)}
              className="flex items-center text-sm text-indigo-600 hover:text-indigo-700"
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Aktualisieren
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Radius (km)
              </label>
              <input
                type="number"
                value={filters.radius}
                onChange={(e) => setFilters({ ...filters, radius: Number(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                min="1"
                max="20"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Min. Ähnlichkeit (%)
              </label>
              <input
                type="number"
                value={filters.minSimilarity}
                onChange={(e) => setFilters({ ...filters, minSimilarity: Number(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                min="0"
                max="100"
              />
            </div>
          </div>
        </div>

        {/* Comparables Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Adresse</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Größe</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Zimmer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Preis</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Miete</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">€/m²</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rendite</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Zustand</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entfernung</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ähnlichkeit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredComparables.map((comp) => (
                  <tr key={comp.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">{comp.address}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{comp.size} m²</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{comp.rooms}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{formatCurrency(comp.price)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatCurrency(comp.rent)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatCurrency(comp.price / comp.size)}</td>
                    <td className="px-4 py-3 text-sm font-medium text-green-600">
                      {formatPercent((comp.rent * 12) / comp.price)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        comp.condition === 'Sehr gut' ? 'bg-green-100 text-green-800' :
                        comp.condition === 'Gut' ? 'bg-blue-100 text-blue-800' :
                        comp.condition === 'Mittel' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {comp.condition}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{comp.distance.toFixed(1)} km</td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex items-center">
                        <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                          <div 
                            className="bg-indigo-600 h-2 rounded-full"
                            style={{ width: `${comp.similarity}%` }}
                          />
                        </div>
                        <span className="text-gray-600">{comp.similarity}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Comparables Scatter Plot */}
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <h3 className="font-semibold text-gray-900 mb-4">Preis-Größen-Verhältnis</h3>
          <Scatter
            data={{
              datasets: [{
                label: 'Vergleichsobjekte',
                data: filteredComparables.map(comp => ({
                  x: comp.size,
                  y: comp.price,
                  r: comp.similarity / 10
                })),
                backgroundColor: 'rgba(99, 102, 241, 0.5)',
                borderColor: 'rgb(99, 102, 241)',
              }]
            }}
            options={{
              responsive: true,
              plugins: {
                tooltip: {
                  callbacks: {
                    label: (context) => [
                      `Größe: ${context.parsed.x} m²`,
                      `Preis: ${formatCurrency(context.parsed.y)}`,
                      `Ähnlichkeit: ${(context.raw as any).r * 10}%`
                    ]
                  }
                }
              },
              scales: {
                x: {
                  title: {
                    display: true,
                    text: 'Größe (m²)'
                  }
                },
                y: {
                  title: {
                    display: true,
                    text: 'Preis (€)'
                  }
                }
              }
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Data Source Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h4 className="font-semibold text-blue-900 mb-1">Demo-Modus: Simulierte Daten</h4>
            <p className="text-sm text-blue-800 mb-2">
              Diese Ansicht nutzt derzeit simulierte Marktdaten zu Demonstrationszwecken.
            </p>
            <p className="text-xs text-blue-700">
              <strong>Echte Datenquellen für Produktion:</strong> ImmoScout24 API, PriceHubble, Scoperty, 
              Destatis, lokale Mietspiegel. Integration erfordert API-Schlüssel und Partnervereinbarungen.
            </p>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="bg-white rounded-lg p-6 border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <MapIcon className="w-6 h-6 mr-2 text-indigo-600" />
            Market Intelligence
          </h2>
          <div className="flex space-x-2">
            {(['overview', 'comparables', 'trends', 'heatmap'] as const).map(view => (
              <button
                key={view}
                onClick={() => setSelectedView(view)}
                className={`px-4 py-2 rounded ${
                  selectedView === view
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {view === 'overview' && 'Übersicht'}
                {view === 'comparables' && 'Vergleichsobjekte'}
                {view === 'trends' && 'Trends'}
                {view === 'heatmap' && 'Heatmap'}
              </button>
            ))}
          </div>
        </div>

        {/* Location Search */}
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="PLZ oder Stadt eingeben..."
                value={searchPostalCode}
                onChange={(e) => setSearchPostalCode(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
          <button
            onClick={() => fetchMarketData(searchPostalCode)}
            disabled={loading}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 flex items-center"
          >
            <Search className="w-5 h-5 mr-2" />
            {loading ? 'Lädt...' : 'Suchen'}
          </button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="bg-white rounded-lg p-12 border border-gray-200 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Marktdaten werden geladen...</p>
        </div>
      ) : (
        <>
          {selectedView === 'overview' && renderOverview()}
          {selectedView === 'comparables' && renderComparables()}
          {selectedView === 'trends' && (
            <div className="bg-white rounded-lg p-6 border border-gray-200">
              <p className="text-gray-600 text-center">Trends-Ansicht kommt bald...</p>
            </div>
          )}
          {selectedView === 'heatmap' && (
            <div className="bg-white rounded-lg p-6 border border-gray-200">
              <p className="text-gray-600 text-center">Heatmap-Ansicht kommt bald...</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MarketIntelligence;